import { computed, effect, Injectable, signal } from '@angular/core';

export type ThemeMode = 'light' | 'system' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
    private readonly STORAGE_KEY = 'alpconnex-theme';

    /** The user-selected mode: light, system, or dark */
    themeMode = signal<ThemeMode>(this.loadThemeMode());

    /** Tracks the OS-level preference (only matters when mode === 'system') */
    private systemPrefersDark = signal(
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );

    /** Resolved boolean — true when the page should render in dark mode */
    isDarkMode = computed(() => {
        const mode = this.themeMode();
        if (mode === 'system') return this.systemPrefersDark();
        return mode === 'dark';
    });

    constructor() {
        // Listen for OS theme changes
        window.matchMedia('(prefers-color-scheme: dark)')
            .addEventListener('change', (e) => this.systemPrefersDark.set(e.matches));

        // Apply theme class immediately
        this.applyTheme(this.isDarkMode());

        // React to any change in the resolved dark mode value
        effect(() => this.applyTheme(this.isDarkMode()));

        // Persist chosen mode
        effect(() => {
            const mode = this.themeMode();
            if (mode === 'system') {
                localStorage.removeItem(this.STORAGE_KEY);
            } else {
                localStorage.setItem(this.STORAGE_KEY, mode);
            }
        });
    }

    setThemeMode(mode: ThemeMode): void {
        this.themeMode.set(mode);
    }

    private applyTheme(dark: boolean): void {
        const root = document.documentElement;
        if (dark) {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }

    private loadThemeMode(): ThemeMode {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored === 'dark' || stored === 'light') return stored;
        return 'system';
    }
}
